import type { ReactNode } from 'react'
import type { Campo, Moneda } from '@rumbo/form'
import { Entrada, Etiqueta, MensajeAyuda, MensajeError, Seleccion } from '@/componentes/ui'

/**
 * Renderiza UN campo del esquema según su `tipo`. Los tipos con lógica
 * propia (origen, institución, tramos, geo_cascada) se manejan aparte en el
 * wizard; este componente cubre los campos "simples" (texto, número, hora,
 * opción, monto, sí/no, casilla).
 */
export function CampoFormulario({
  campo,
  valor,
  onCambio,
  error,
  obligatorio,
}: {
  campo: Campo
  valor: unknown
  onCambio: (v: unknown) => void
  error?: string
  obligatorio: boolean
}) {
  const id = `campo-${campo.id}`

  if (campo.tipo === 'si_no') {
    return (
      <fieldset>
        <legend className="mb-1.5 text-[15px] font-medium text-texto">{campo.etiqueta}</legend>
        <div className="flex gap-3">
          {[
            { v: true, t: 'Sí' },
            { v: false, t: 'No' },
          ].map((o) => (
            <button
              key={String(o.v)}
              type="button"
              onClick={() => onCambio(o.v)}
              className={`objetivo-tactil foco-visible flex-1 rounded-[var(--radius-rumbo)] border px-4 py-2.5 text-sm font-medium ${
                valor === o.v ? 'border-azul bg-azul-suave text-azul' : 'border-borde-fuerte text-texto-suave hover:bg-superficie-alt'
              }`}
            >
              {o.t}
            </button>
          ))}
        </div>
      </fieldset>
    )
  }

  if (campo.tipo === 'casilla') {
    return (
      <label className="objetivo-tactil foco-visible flex cursor-pointer items-start gap-3 rounded-[var(--radius-rumbo)] border border-borde-fuerte p-3.5">
        <input type="checkbox" checked={Boolean(valor)} onChange={(e) => onCambio(e.target.checked)} className="mt-0.5 size-5 accent-[var(--mppt-azul-marino)]" />
        <span className="text-sm text-texto">{campo.etiqueta}</span>
      </label>
    )
  }

  if (campo.tipo === 'opcion') {
    return (
      <fieldset>
        <legend className="mb-1.5 text-[15px] font-medium text-texto">
          {campo.etiqueta}
          {obligatorio && <span className="ml-1 text-rojo">*</span>}
        </legend>
        {campo.ayuda && <p className="mb-2 text-sm text-texto-suave">{campo.ayuda}</p>}
        <div className="grid gap-2 sm:grid-cols-2">
          {campo.opciones?.map((o) => (
            <button
              key={o.valor}
              type="button"
              onClick={() => onCambio(o.valor)}
              className={`objetivo-tactil foco-visible rounded-[var(--radius-rumbo)] border px-3.5 py-2.5 text-left text-sm ${
                valor === o.valor ? 'border-azul bg-azul-suave font-medium text-azul' : 'border-borde-fuerte text-texto hover:bg-superficie-alt'
              }`}
            >
              {o.etiqueta}
              {o.ayuda && <span className="block text-xs text-texto-tenue">{o.ayuda}</span>}
            </button>
          ))}
        </div>
        <MensajeError>{error}</MensajeError>
      </fieldset>
    )
  }

  if (campo.tipo === 'multiple') {
    const seleccion = Array.isArray(valor) ? (valor as string[]) : []
    const ayuda = campo.ayuda ?? (campo.min != null || campo.max != null
      ? `Elija entre ${campo.min ?? 1} y ${campo.max ?? 'varias'} opciones.`
      : undefined)
    return (
      <fieldset>
        <legend className="mb-1.5 text-[15px] font-medium text-texto">
          {campo.etiqueta}
          {obligatorio && <span className="ml-1 text-rojo">*</span>}
        </legend>
        {ayuda && <p className="mb-2 text-sm text-texto-suave">{ayuda}</p>}
        <div className="grid gap-2 sm:grid-cols-2">
          {campo.opciones?.map((o) => {
            const marcado = seleccion.includes(o.valor)
            const bloqueadoPorMax = !marcado && campo.max !== undefined && seleccion.length >= campo.max
            return (
              <label
                key={o.valor}
                className={`objetivo-tactil foco-visible flex cursor-pointer items-center gap-2 rounded-[var(--radius-rumbo)] border px-3.5 py-2.5 text-sm ${marcado ? 'border-azul bg-azul-suave' : 'border-borde-fuerte'} ${bloqueadoPorMax ? 'opacity-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={marcado}
                  disabled={bloqueadoPorMax}
                  onChange={(e) => onCambio(e.target.checked ? [...seleccion, o.valor] : seleccion.filter((x) => x !== o.valor))}
                  className="size-4 accent-[var(--mppt-azul-marino)]"
                />
                {o.etiqueta}
              </label>
            )
          })}
        </div>
        <MensajeError>{error}</MensajeError>
      </fieldset>
    )
  }

  if (campo.tipo === 'hora') {
    return (
      <div>
        <Etiqueta htmlFor={id} obligatorio={obligatorio} ayuda={campo.ayuda}>
          {campo.etiqueta}
        </Etiqueta>
        <Entrada id={id} type="time" value={(valor as string) ?? ''} onChange={(e) => onCambio(e.target.value)} error={error} />
        <MensajeError>{error}</MensajeError>
      </div>
    )
  }

  if (campo.tipo === 'entero') {
    return (
      <div>
        <Etiqueta htmlFor={id} obligatorio={obligatorio} ayuda={campo.ayuda}>
          {campo.etiqueta}
        </Etiqueta>
        <Entrada
          id={id}
          type="number"
          inputMode="numeric"
          min={campo.min}
          max={campo.max}
          value={(valor as number | undefined) ?? ''}
          onChange={(e) => onCambio(e.target.value === '' ? undefined : Number(e.target.value))}
          error={error}
        />
        <MensajeError>{error}</MensajeError>
      </div>
    )
  }

  if (campo.tipo === 'monto') {
    const v = (valor as { monto?: string; moneda?: Moneda }) ?? {}
    // Si la etiqueta tiene prefijo "Ida —", "Regreso —", "Mensual —" (ver
    // esquema.ts), lo pintamos en negrita para que el encuestado identifique
    // de un vistazo a qué viaje se refiere la pregunta.
    const partes = campo.etiqueta.split(' — ')
    const etiquetaRender: ReactNode = partes.length > 1
      ? <><strong>{partes[0]}</strong>{' — ' + partes.slice(1).join(' — ')}</>
      : campo.etiqueta
    return (
      <div>
        <Etiqueta htmlFor={id} obligatorio={obligatorio} ayuda={campo.ayuda}>
          {etiquetaRender}
        </Etiqueta>
        {/*
          Width controlado desde los wrappers: Entrada y Seleccion llevan `w-full`
          en claseCampo (ui.tsx). Si acá se pasa className="flex-1" o "w-28"
          directamente al input/select, el `w-full` igual gana en el CSS generado
          por Tailwind y los dos elementos compiten → el select ocupa casi todo
          y el input queda ~0px (parece que "no deja escribir"). Delegar el
          sizing a divs padres (min-w-0 permite shrink dentro de flex) deja a
          cada hijo con su `w-full` interno sin conflicto.
        */}
        <div className="flex gap-2">
          <div className="min-w-0 flex-1">
            <Entrada
              id={id}
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={v.monto ?? ''}
              onChange={(e) => onCambio({ monto: e.target.value, moneda: v.moneda ?? 'VES' })}
              error={error}
            />
          </div>
          <div className="w-28 shrink-0">
            <Seleccion aria-label="Moneda" value={v.moneda ?? 'VES'} onChange={(e) => onCambio({ monto: v.monto ?? '0', moneda: e.target.value as Moneda })}>
              {campo.opciones?.map((o) => (
                <option key={o.valor} value={o.valor}>
                  {o.etiqueta}
                </option>
              ))}
            </Seleccion>
          </div>
        </div>
        <MensajeError>{error}</MensajeError>
      </div>
    )
  }

  // texto (por defecto)
  return (
    <div>
      <Etiqueta htmlFor={id} obligatorio={obligatorio} ayuda={campo.ayuda}>
        {campo.etiqueta}
      </Etiqueta>
      <Entrada id={id} type="text" maxLength={campo.maxLargo} value={(valor as string) ?? ''} onChange={(e) => onCambio(e.target.value)} error={error} />
      <MensajeAyuda />
      <MensajeError>{error}</MensajeError>
    </div>
  )
}
